import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { ExternalLink, CheckCircle2, Sparkles, AlertTriangle } from "lucide-react";

const formatLimit = (v) => (v == null ? "Unlimited" : v.toLocaleString());

// Everything a Done-For-You merchant unlocks — the full Growth-tier stack.
const INCLUDED = [
  "Your branded pass in Apple Wallet + Google Wallet",
  "Unlimited pass holders, stamps and rewards",
  "Unlimited campaigns with lock-screen pushes",
  "Staff scanner on unlimited devices — no logins, no app",
  "SMS invites to bring your regulars onboard",
  "Live analytics: repeat visits, redemptions, QR funnel",
  "Design changes handled by your concierge within 24 hours",
  "Priority support straight from the BackPocket team",
];

export default function Billing() {
  const { user } = useAuth();
  if (user && !user.is_superadmin) return <Navigate to="/app" replace />;
  return <BillingInner />;
}

function BillingInner() {
  const { activeOrg } = useAuth();
  const [usage, setUsage] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const load = () => {
    if (!activeOrg) return;
    api.get(`/orgs/${activeOrg.id}/plan-usage`).then((r) => setUsage(r.data)).catch(() => {});
  };
  useEffect(load, [activeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Returning from Stripe Checkout: poll until the webhook/status flip lands.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("checkout") === "success" && sid) {
      window.history.replaceState({}, "", "/app/billing");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") { toast.success("Auto-pay is set up — you're all done."); load(); return; }
        } catch (_) {}
        if (tries < 8) setTimeout(poll, 2000);
      };
      poll();
    } else if (params.get("checkout") === "cancel") {
      window.history.replaceState({}, "", "/app/billing");
      toast.info("Checkout cancelled — no card was saved.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!activeOrg) return null;

  const startAutoPay = async () => {
    setCheckoutBusy(true);
    try {
      const { data } = await api.post(`/orgs/${activeOrg.id}/billing/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setCheckoutBusy(false); }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post(`/orgs/${activeOrg.id}/billing/portal`, { return_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(apiError(e)); setPortalBusy(false); }
  };

  const requestCancel = async () => {
    setCancelBusy(true);
    try {
      await api.post(`/orgs/${activeOrg.id}/billing/cancel-request`);
      toast.success("Cancellation requested — we'll be in touch shortly.");
      setConfirmingCancel(false);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setCancelBusy(false); }
  };

  const withdrawCancel = async () => {
    setCancelBusy(true);
    try {
      await api.post(`/orgs/${activeOrg.id}/billing/cancel-withdraw`);
      toast.success("Welcome back — cancellation withdrawn.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setCancelBusy(false); }
  };

  const sub = usage?.subscription || {};
  const status = sub.status || "active";
  const price = sub.price ?? 49.99;
  const cancelRequested = !!sub.cancel_requested_at;
  const hasStripeSub = !!sub.stripe_subscription_id;

  return (
    <div className="max-w-4xl">
      <PageHeader title="Billing" subtitle="Your plan, your usage, all in one place." />

      {/* Current plan */}
      <div className="rounded-3xl bg-[#0B0D10] text-white p-8 grain relative overflow-hidden" data-testid="current-plan-card">
        <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#C8FF4D]/12 blur-3xl" />
        <div className="relative z-10 flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[#C8FF4D]">
              <Sparkles size={11} strokeWidth={2.5} /> Done-For-You
            </div>
            <div className="font-display font-extrabold tracking-[-0.02em] mt-3" style={{ fontSize: "clamp(2.6rem,4.5vw,3.4rem)", lineHeight: 0.9 }} data-testid="billing-price">
              ${price}<span className="text-white/45 text-lg">/mo AUD</span>
            </div>
            <p className="text-white/60 mt-2 max-w-md text-[13.5px] leading-relaxed">Everything designed, built and maintained by the BackPocket team. You run the counter, we run the rest.</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-[0.14em] rounded-full border border-white/15 px-2.5 py-0.5 text-white/75" data-testid="billing-status">{status}</span>
              {["trial", "trialing"].includes(status) && sub.trial_ends && (
                <span className="text-[12.5px] text-white/55">Free trial ends {new Date(sub.trial_ends).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
              )}
              {status === "active" && sub.current_period_end && (
                <span className="text-[12.5px] text-white/55">Renews {new Date(sub.current_period_end).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
              )}
            </div>
            {hasStripeSub ? (
              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <span className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#C8FF4D]/15 border border-[#C8FF4D]/30 text-[#C8FF4D] text-[12.5px] font-semibold" data-testid="billing-autopay-active">
                  <CheckCircle2 size={13} /> Auto-pay active
                </span>
                <button onClick={openPortal} disabled={portalBusy} data-testid="billing-portal"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/[0.07] hover:bg-white/10 text-white/85 text-[13px] font-medium border border-white/10">
                  <ExternalLink size={14} />{portalBusy ? "Opening…" : "Manage payment method"}
                </button>
              </div>
            ) : (
              <div className="mt-5">
                <button onClick={startAutoPay} disabled={checkoutBusy} data-testid="billing-autopay-setup"
                  className="btn-lift inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C8FF4D] hover:bg-[#b8ef3d] text-[#0B0D10] text-[13.5px] font-semibold disabled:opacity-60">
                  {checkoutBusy ? "Opening secure checkout…" : <>Set up auto-pay <ExternalLink size={14} /></>}
                </button>
                <p className="text-[12px] text-white/45 mt-2.5 max-w-sm leading-relaxed">Add your card once via Stripe — your monthly is charged automatically from then on. Cancel anytime.</p>
              </div>
            )}
          </div>
          <div className="min-w-[280px] flex-1 max-w-md rounded-2xl bg-white/[0.04] border border-white/10 p-5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 mb-4">This month's usage</div>
            {usage ? (
              <div className="space-y-4 text-white">
                {[
                  ["Active pass holders", usage.usage.pass_holders],
                  ["Campaigns this month", usage.usage.campaigns_this_month],
                  ["Locations", usage.usage.locations],
                ].map(([lbl, u]) => (
                  <div key={lbl}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-[12.5px] text-white/70">{lbl}</span>
                      <span className="text-[12.5px] font-medium tabular-nums text-white">{u.used.toLocaleString()}<span className="text-white/40"> / {formatLimit(u.limit)}</span></span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                      {u.limit == null ? (<div className="h-full w-full bg-[#C8FF4D]/60" />) : (
                        <div className="h-full rounded-full bg-[#C8FF4D]" style={{ width: `${Math.min(100, Math.round((u.used / u.limit) * 100))}%` }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="h-24 rounded-lg bg-white/[0.06] animate-pulse" />}
          </div>
        </div>
      </div>

      {/* What's included */}
      <div className="mt-6 rounded-3xl bg-white border border-[#E8E5DE] p-8" data-testid="billing-included">
        <div className="flex items-baseline justify-between mb-5 gap-4 flex-wrap">
          <h3 className="font-display font-bold text-[18px] tracking-[-0.01em]">Everything in your plan</h3>
          <span className="text-[12px] text-[#3a3d44]/50">One flat monthly · no usage caps · cancel anytime</span>
        </div>
        <ul className="grid sm:grid-cols-2 gap-y-2.5 gap-x-6">
          {INCLUDED.map((line) => (
            <li key={line} className="flex items-start gap-3 text-[13.5px] text-[#0B0D10]/85">
              <span className="mt-[3px] grid place-items-center h-4 w-4 rounded-full shrink-0 bg-[#0B0D10] text-[#C8FF4D]">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none"><path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <span className="leading-snug">{line}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Cancel */}
      {cancelRequested ? (
        <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3" data-testid="billing-cancel-requested">
          <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-[14px] font-semibold text-amber-900">Cancellation requested</div>
            <p className="text-[13px] text-amber-800/80 mt-1 leading-relaxed">
              We received your request on {new Date(sub.cancel_requested_at).toLocaleDateString("en-AU", { day: "numeric", month: "long" })}. Someone from the team will confirm with you shortly — your program and your customers' passes stay live in the meantime.
            </p>
            <button onClick={withdrawCancel} disabled={cancelBusy} data-testid="billing-cancel-withdraw"
              className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#0B0D10] text-white text-[12.5px] font-semibold disabled:opacity-60">
              {cancelBusy ? "One sec…" : "Changed my mind — keep my plan"}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl bg-[#F4F1E9] border border-[#E8E5DE] p-5" data-testid="billing-cancel-section">
          {!confirmingCancel ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-[#0B0D10] mt-0.5 shrink-0" />
                <div className="text-[12.5px] text-[#3a3d44] leading-relaxed max-w-lg">No lock-in contracts. If BackPocket isn't working for your business, you can cancel any time.</div>
              </div>
              <button onClick={() => setConfirmingCancel(true)} data-testid="billing-cancel-open"
                className="text-[12.5px] text-[#3a3d44]/60 hover:text-red-600 font-medium transition-colors">
                Cancel subscription
              </button>
            </div>
          ) : (
            <div data-testid="billing-cancel-confirm">
              <div className="text-[14px] font-semibold text-[#0B0D10]">Cancel your subscription?</div>
              <p className="text-[13px] text-[#3a3d44]/75 mt-1 leading-relaxed max-w-lg">
                Your pass, your customers' stamps and all your data stay safe — nothing is deleted. We'll confirm the cancellation with you personally within 24 hours.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <button onClick={requestCancel} disabled={cancelBusy} data-testid="billing-cancel-submit"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-red-600 hover:bg-red-700 text-white text-[12.5px] font-semibold disabled:opacity-60">
                  {cancelBusy ? "Sending…" : "Yes, request cancellation"}
                </button>
                <button onClick={() => setConfirmingCancel(false)} className="text-[12.5px] text-[#3a3d44] hover:text-[#0B0D10]">Keep my plan</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
